import { getRecordKeys } from '@m43/core';
import {
    AssignedParticipantUnreadInfo,
    ConversationMemberView,
    ConversationParticipantView,
    ConversationView,
} from '@mark43/cad-api';
import { formatMonthDayHoursMinutes } from '../../common/helpers/utils';
import { alphabeticalStringSort } from '../../common/sorting/sorts';
import { ConversationKey } from '../models';
import { buildParticipantKey } from '../store/helpers';
import { ParticipantFiltersInfo, ParticipantIdentities } from '../store/types';
import { ConversationItemData } from '../types';
import { sortConversationKeys } from './array-helpers';
import { getShortLabel } from './conversation-participant-helpers';
import { participantKeysMatch } from './conversations-filter-helpers';

// getConversationListKeys returns the a list of conversationKeys for all the conversations in the sidepanel for
// the currectly selected filter
export const getConversationListKeys = (
    selectedFilterParticipants: ConversationParticipantView[],
    participantFiltersInfo?: ParticipantFiltersInfo,
    conversations?: Record<number, ConversationView>
): ConversationKey[] => {
    if (participantFiltersInfo === undefined || conversations === undefined) {
        return [];
    }
    const conversationListKeys: ConversationKey[] = [];
    const myAssignedParticipantUnreadInfoByConversationIdMap: Record<
        number,
        AssignedParticipantUnreadInfo
    > = {};
    selectedFilterParticipants.forEach((participant) => {
        return participantFiltersInfo?.[
            buildParticipantKey(participant.id, participant.entityType)
        ]?.conversationIds.forEach((conversationId) => {
            const conversationKey = new ConversationKey(conversationId, participant);
            const assignedParticipantUnreadInfos =
                conversations?.[conversationId]?.assignedParticipantUnreadInfos;
            const myAssignedParticipantUnreadInfo = assignedParticipantUnreadInfos?.find(
                (unreadInfo) =>
                    unreadInfo.participantId === participant.id &&
                    unreadInfo.entityType === participant.entityType
            );
            if (myAssignedParticipantUnreadInfo !== undefined) {
                myAssignedParticipantUnreadInfoByConversationIdMap[conversationId] =
                    myAssignedParticipantUnreadInfo;
            }

            conversationListKeys.push(conversationKey);
        });
    });

    return conversationListKeys.sort(
        sortConversationKeys(conversations, myAssignedParticipantUnreadInfoByConversationIdMap)
    );
};

export const getBookmarkedConversationListKeys = (
    bookmarkedConversations?: Record<number, ConversationView>
): ConversationKey[] => {
    if (bookmarkedConversations === undefined) {
        return [];
    }
    const conversationIds = getRecordKeys(bookmarkedConversations);
    const conversationListKeys = conversationIds.flatMap((conversationId) => {
        const conversation = bookmarkedConversations[conversationId];
        return conversation.assignedParticipantUnreadInfos.map(
            ({ participantId, entityType, label }) =>
                new ConversationKey(conversationId, { id: participantId, entityType, label }, true)
        );
    });
    return conversationListKeys.sort(sortConversationKeys(bookmarkedConversations));
};

// return true if conversation is active, false if inactive
export const isConversationActive = (
    conversationKey?: ConversationKey,
    participantIdentities?: ParticipantIdentities
) => {
    if (
        conversationKey?.participantIdentity === undefined ||
        participantIdentities?.active === undefined
    ) {
        return false;
    }
    const { participantIdentity } = conversationKey;
    const foundIdentity = participantIdentities.active.find((identity) => {
        return participantKeysMatch(identity, participantIdentity);
    });
    return foundIdentity !== undefined;
};

// return the index of the selected conversation from the list of conversations in the sidebar
export const getSelectedConversationIndex = (
    conversationListKeys: ConversationKey[],
    selectedConversationKey?: ConversationKey
) => {
    return conversationListKeys.findIndex((conversationKey) =>
        selectedConversationKey?.equals(conversationKey)
    );
};

// return the data needed to render conversation list items in the side panel
export const getConversations = (
    conversationListKeys: ConversationKey[],
    conversations: Record<number, ConversationView> | undefined
) =>
    conversationListKeys.reduce((output: ConversationItemData[], conversationKey) => {
        const { conversationId, participantIdentity } = conversationKey;
        if (conversations === undefined || participantIdentity === undefined) {
            return output;
        }
        const conversation = conversations[conversationId];
        if (conversation === undefined) {
            return output;
        }
        const { members } = conversation;
        const myParticipantUnreadInfo = conversation.assignedParticipantUnreadInfos.find(
            ({ participantId, entityType }) =>
                participantId === participantIdentity.id &&
                entityType === participantIdentity.entityType
        );
        const unreadCount = myParticipantUnreadInfo?.unreadCount;
        const hasUrgentUnread = myParticipantUnreadInfo?.hasUrgentUnread;
        const isConversationForSelf =
            members.length === 1 &&
            members[0].id === participantIdentity.id &&
            members[0].entityType === participantIdentity.entityType;
        const allOtherMembers = isConversationForSelf
            ? members
            : members
                  .filter(
                      (member) =>
                          !(
                              member.id === participantIdentity.id &&
                              member.entityType === participantIdentity.entityType
                          )
                  )
                  .sort((a, b) => alphabeticalStringSort(a.label, b.label));
        const allOtherMembersLabel = allOtherMembers
            .map((member) => getShortLabel(member))
            .join(', ');
        const allOtherMembersTooltip = allOtherMembers
            .map((member) => getShortLabel(member))
            .join(', ');
        const lastMessageDateTimeDisplay = conversation.newestMessageDateUtc
            ? formatMonthDayHoursMinutes(conversation.newestMessageDateUtc)
            : '';
        output.push({
            conversationKey,
            allOtherMembersLabel,
            allOtherMembersTooltip,
            senderParticipantLabel: getShortLabel(participantIdentity),
            lastMessageDateTimeDisplay,
            unreadCount,
            hasUrgentUnread,
        });
        return output;
    }, []);

export const findExistingConversationForMembers = (
    members: ConversationMemberView[],
    conversations?: Record<number, ConversationView>
): ConversationView | undefined => {
    if (!conversations || !members.length) {
        return;
    }

    const newConversationMembersSortedIds = members.map((member) => member.id).sort();
    return Object.values(conversations).find((conversation) => {
        const conversationMembersSortedIds = conversation.members.map((member) => member.id).sort();
        return (
            newConversationMembersSortedIds?.join(',') === conversationMembersSortedIds.join(',')
        );
    });
};
